import { StrictMode } from 'react';

import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import App from './app/App';
import { persistor, store } from './app/store';
import i18next from './common/configs/i18n.config';
import { resetReloadGuardForFreshNavigation } from './common/utils/retryDynamicImport';

// Re-arm the chunk-reload guard on a fresh page load (never on our own recovery
// reload) so each page-load session gets a single stale-cache reload budget.
resetReloadGuardForFreshNavigation();

// alert if appconfig is not loaded
if (!globalThis?.appConfig?.env) {
  console.error(
    `Failed to load appConfig: No config found for environment '${process.env.NODE_ENV}'`,
    globalThis?.appConfig
  );
}

const disableReactDevTools = () => {
  // Check if the React Developer Tools global hook exists
  if (typeof window.__REACT_DEVTOOLS_GLOBAL_HOOK__ !== 'object') {
    return;
  }

  for (const prop in window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    if (prop === 'renderers') {
      window.__REACT_DEVTOOLS_GLOBAL_HOOK__[prop] = new Map();
    } else {
      // Replace all of its properties with a no-op function or a null value
      // depending on their types

      window.__REACT_DEVTOOLS_GLOBAL_HOOK__[prop] =
        typeof window.__REACT_DEVTOOLS_GLOBAL_HOOK__[prop] === 'function'
          ? () => {
              //
            }
          : null;
    }
  }
};

if (process.env.NODE_ENV === 'production') {
  // disable console logs in production
  disableReactDevTools();
}

const app = document.getElementById('root');

// create a root
const root = createRoot(app as Element);
root.render(
  <StrictMode>
    <I18nextProvider i18n={i18next}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <App />
        </PersistGate>
      </Provider>
    </I18nextProvider>
  </StrictMode>
);
